import React from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik, Field } from 'formik'
import { useLogin } from 'lib/core/hooks'
import { Button, Form, Toast } from 'lib/ui/components'
import { Wrapper, ForgotPassword } from './components/Styled'
import { schema, validation } from './schema'
import { InterceptBlurEvent } from 'lib/ui/components/Form/types'
import { captureEmail } from 'modules/listrak'

interface Props {
  shouldRedirectToAccount?: boolean
  avoidRedirect?: boolean
  setCheckoutState?: (param: string) => void
}

export interface FormValues {
  email: string
  password: string
}

export const LoginForm: React.FC<Props> = ({ shouldRedirectToAccount, avoidRedirect, setCheckoutState }) => {
  const history = useHistory<{ from?: any, AddToWishlist?: boolean } | undefined>()
  const { pathname } = useLocation()
  const { formatMessage } = useIntl()

  const { login, error, isFetching } = useLogin({
    onSuccess: handleSuccess,
    onFailure: handleFailure
  })

  const fromPath = history.location.state?.from
  const isAddToWishlist = history.location.state?.AddToWishlist

  const handleCreateAccountClick = () => {
    if (avoidRedirect) {
      setCheckoutState?.('create')
    } else {
      history.push('/customer/account/create')
    }
  }

  function handleSuccess () {
    if (avoidRedirect) {
      return
    }

    if (fromPath) {
      history.push({
        pathname: fromPath?.pathname,
        state: {
          from: pathname,
          AddToWishlist: isAddToWishlist
        }
      })
    } else {
      if (shouldRedirectToAccount) {
        history.replace('/customer/account')
      } else {
        history.push('/')
      }
    }
  }

  function handleFailure () {
    Toast.toastFailure({ messageId: error })
  }

  function handleSubmit (values: FormValues) {
    login(values)
  }

  return (
    <Wrapper>
      <Formik
        initialValues={schema}
        validationSchema={validation(formatMessage)}
        onSubmit={handleSubmit}
      >
        <Form.Wrapper>
          <Field
            name='email'
            label={formatMessage({
              id: 'customer.account.login.label.email'
            })}
            component={Form.Input}
            interceptBlur={(e: InterceptBlurEvent<string>) => {
              captureEmail(e.value)
            }}
            isRequired
            autoFocus
          />

          <Field
            name='password'
            label={formatMessage({
              id: 'customer.account.login.label.password'
            })}
            component={Form.Input}
            type='password'
            isRequired
          />
          <ForgotPassword>
            <Link to='/customer/account/forgotpassword'>
              <FormattedMessage
                id='action.forgotPassword'
              />
            </Link>
          </ForgotPassword>

          <Form.Actions.Wrapper>
            <Button
              type='submit'
              isLoading={isFetching}
            >
              <FormattedMessage
                id='action.login'
              />
            </Button>

            <Button
              className='outlined'
              skin={Button.SKIN.OUTLINE}
              isDisabled={isFetching}
              onClick={handleCreateAccountClick}
            >
              <FormattedMessage
                id='action.createAccount'
              />
            </Button>
          </Form.Actions.Wrapper>
        </Form.Wrapper>
      </Formik>
    </Wrapper>
  )
}
